import { ChevronDownIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { ALL_USERS_KEY, ALL_USERS_LABEL } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { DashboardSelectedUser } from "@/types";

export const DashboardUserSelector = ({
  value,
  onValueChange,
  triggerClassName,
  includeAllUsers = true,
  screen = MIXPANEL_SCREENS.DASHBOARD,
}: {
  value: DashboardSelectedUser;
  onValueChange: (value: DashboardSelectedUser) => void;
  triggerClassName?: string;
  includeAllUsers?: boolean;
  screen?: MIXPANEL_SCREENS;
}) => {
  const { userFirstNameMap, userFirstNameOptions } = useTaggableUsers();

  /* Handlers */
  const handleSelect = (next: DashboardSelectedUser) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      screen,
      "Staffer",
      String(next),
    );
    onValueChange(next);
  };

  /* Helpers */
  const selectedLabel =
    value === ALL_USERS_KEY
      ? ALL_USERS_LABEL
      : (userFirstNameMap[value] ?? "Unknown");

  /* Render */
  const render = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            "h-9 max-w-[220px] gap-1.5 rounded-full",
            value !== ALL_USERS_KEY &&
              "border-lever-border-light bg-lever-surface-default-on-elevation text-lever-text-heading",
            triggerClassName,
          )}
          size="sm"
          type="button"
          variant="outlineSecondary"
        >
          <span className="truncate">Staffer: {selectedLabel}</span>
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-lever-text-label" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[320px] min-w-[200px] overflow-auto"
      >
        {includeAllUsers ? (
          <>
            <DropdownMenuItem
              onSelect={() => {
                handleSelect(ALL_USERS_KEY);
              }}
            >
              {ALL_USERS_LABEL}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        {userFirstNameOptions.map((user) => (
          <DropdownMenuItem
            key={user.value}
            onSelect={() => {
              handleSelect(user.value);
            }}
          >
            {user.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return render();
};
